import { loadStripe, Stripe } from "@stripe/stripe-js";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router";
import CheckoutPaymentForm, { CheckoutFormData } from "../components/checkout-payment-form";
import CheckoutSuccess from "../components/checkout-success";
import CurrencyPicker from "../components/currency-picker";
import Header from "../components/header";
import { useCheckoutDetails, trackPaymentLinkVisit, PublicCheckoutProduct, PublicCheckoutProductPrice } from "../data/checkout";
import { isColorLight, getContrastColor } from "../utils/colors";
import { detectPreferredCurrency, resolveCheckoutCurrency } from "../utils/currency";
import { formatCurrency } from "../utils/format";
import { getTrackingMetadata } from "../utils/tracking";

function CheckoutPayment() {
  const { t } = useTranslation();
  const { paymentLinkId } = useParams<{ paymentLinkId: string }>();

  // TanStack Query for checkout data
  const {
    data: checkoutData,
    isLoading,
    isError,
    error,
    refetch,
  } = useCheckoutDetails(paymentLinkId);

  // Local state for Stripe and UI
  const [stripeInstance, setStripeInstance] = useState<Stripe | null>(null);
  const [isStripeLoading, setIsStripeLoading] = useState(true);
  // `loadStripe` returns null when the Stripe JS script fails to load
  // (ad-blocker, privacy extension, CSP, offline, etc.). Without
  // tracking this explicitly, the page would fall through to the
  // `return null` fallback below and the user would see a blank
  // screen. Use this flag to render a recoverable error instead.
  const [stripeInitFailed, setStripeInitFailed] = useState(false);
  const [paymentSuccess, setPaymentSuccess] = useState(false);
  const [discountedAmount, setDiscountedAmount] = useState<number | null>(null);
  const [selectedCurrency, setSelectedCurrency] = useState<string | null>(null);
  const formDataRef = useRef<Partial<CheckoutFormData>>({});

  const handleFormDataChange = useCallback((data: CheckoutFormData) => {
    formDataRef.current = data;
  }, []);

  // Determine available currencies and resolve selection
  const availableCurrencies = useMemo(
    () => checkoutData?.availableCurrencies ?? [],
    [checkoutData],
  );
  const defaultCurrency = checkoutData?.defaultCurrency ?? checkoutData?.products[0]?.price.currency ?? "eur";

  // Initialize currency once when checkout data arrives
  useEffect(() => {
    if (checkoutData && !selectedCurrency) {
      const preferred = detectPreferredCurrency();
      const resolved = resolveCheckoutCurrency(
        availableCurrencies,
        defaultCurrency,
        preferred,
      );
      setSelectedCurrency(resolved);
    }
  }, [checkoutData, availableCurrencies, defaultCurrency, selectedCurrency]);

  const handleCurrencyChange = useCallback((currency: string) => {
    setSelectedCurrency(currency);
    // Persist for 3DS redirect recovery
    try {
      sessionStorage.setItem("gymkee_checkout_currency", currency);
    } catch {
      // sessionStorage not available
    }
    // Reset payment state when currency changes
    setDiscountedAmount(null);
  }, []);

  /**
   * Get the price for a product matching the selected currency.
   * Falls back to the legacy `price` field if no match in `prices` array.
   */
  const getProductPrice = useCallback(
    (product: PublicCheckoutProduct): PublicCheckoutProductPrice => {
      const target = selectedCurrency || defaultCurrency;
      if (product.prices?.length) {
        const match = product.prices.find(
          (p) => p.currency.toLowerCase() === target.toLowerCase(),
        );
        if (match) return match;
        // Fallback: first price
        return product.prices[0];
      }
      // Legacy: use `price` field
      return product.price;
    },
    [selectedCurrency, defaultCurrency],
  );

  const secondaryColor = checkoutData?.business.secondaryColor || "#FFF7F0";

  const isLightBg = isColorLight(secondaryColor);
  const textColor = getContrastColor(secondaryColor);
  const textMutedColor = isLightBg
    ? "rgba(0,0,0,0.6)"
    : "rgba(255,255,255,0.7)";

  // Track visit when checkout page loads with valid data
  useEffect(() => {
    if (!paymentLinkId || !checkoutData) return;
    getTrackingMetadata().then((metadata) => {
      trackPaymentLinkVisit(paymentLinkId, metadata);
    });
  }, [paymentLinkId, checkoutData]);

  // Load Stripe and handle 3DS redirect when checkout data is available
  useEffect(() => {
    if (!checkoutData) return;

    const stripeAccountId = checkoutData.stripeAccountId;
    let cancelled = false;

    async function initStripe() {
      try {
        const stripe = await loadStripe(
          import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY || "",
          { stripeAccount: stripeAccountId },
        );
        if (cancelled) return;

        // `loadStripe` returning null is the common silent-failure
        // mode: ad-blocker / privacy extension / CSP / offline. We
        // surface a real error state instead of falling through to a
        // blank page.
        if (!stripe) {
          setStripeInitFailed(true);
          setIsStripeLoading(false);
          return;
        }

        setStripeInstance(stripe);

        // Check if returning from 3DS redirect
        const urlParams = new URLSearchParams(window.location.search);
        const paymentIntentClientSecret = urlParams.get(
          "payment_intent_client_secret",
        );

        if (paymentIntentClientSecret) {
          try {
            const { paymentIntent } = await stripe.retrievePaymentIntent(
              paymentIntentClientSecret,
            );
            if (cancelled) return;

            if (
              paymentIntent?.status === "succeeded" ||
              paymentIntent?.status === "processing"
            ) {
              if (paymentIntent.amount) {
                setDiscountedAmount(paymentIntent.amount);
              }
              setPaymentSuccess(true);
              window.history.replaceState({}, "", window.location.pathname);
            }
          } catch (retrieveErr) {
            // Don't block checkout if 3DS recovery fails — the form
            // is still usable. Log and move on.
            console.warn("[checkout] retrievePaymentIntent failed", retrieveErr);
          }
        }
      } catch (err) {
        if (cancelled) return;
        console.error("[checkout] Stripe init failed", err);
        setStripeInitFailed(true);
      } finally {
        if (!cancelled) setIsStripeLoading(false);
      }
    }

    initStripe();

    return () => {
      cancelled = true;
    };
  }, [checkoutData]);

  const handlePaymentSuccess = () => {
    setPaymentSuccess(true);
  };

  const handleAmountChange = (amount: number) => {
    setDiscountedAmount(amount);
  };

  // Derive error message from query error
  const getErrorMessage = () => {
    if (!paymentLinkId) return t("gymkeePay.checkout.productNotFound");
    if (error) {
      const axiosError = error as { response?: { status?: number } };
      if (axiosError.response?.status === 404) {
        return t("gymkeePay.checkout.productNotFound");
      }
      return t("gymkeePay.checkout.failedToLoad", {
        defaultValue: "Failed to load checkout. Please try again.",
      });
    }
    return t("gymkeePay.checkout.failedToLoad", {
      defaultValue: "Failed to load checkout. Please try again.",
    });
  };

  // Loading state
  if (isLoading || (checkoutData && isStripeLoading)) {
    return (
      <div className="min-h-screen bg-gray-100">
        <div className="absolute left-0 top-0 p-6">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 animate-pulse rounded-lg bg-gray-300"></div>
            <div className="h-5 w-32 animate-pulse rounded bg-gray-300"></div>
          </div>
        </div>

        <div className="flex min-h-screen flex-col items-center justify-center px-4 py-20">
          <div className="mb-4 w-full max-w-md rounded-xl bg-white p-6 shadow-lg">
            <div className="mb-4">
              <div className="h-6 w-48 animate-pulse rounded bg-gray-200"></div>
              <div className="mt-2 h-4 w-64 animate-pulse rounded bg-gray-200"></div>
            </div>
            <div className="h-9 w-36 animate-pulse rounded bg-gray-200"></div>
          </div>

          <div className="w-full max-w-md rounded-xl bg-white p-6 shadow-lg">
            <div className="space-y-4">
              <div className="h-11 w-full animate-pulse rounded-lg bg-gray-200"></div>
              <div className="h-11 w-full animate-pulse rounded-lg bg-gray-200"></div>
              <div className="h-11 w-full animate-pulse rounded-lg bg-gray-200"></div>
            </div>
            <div className="mt-4 h-12 w-full animate-pulse rounded-lg bg-gray-300"></div>
          </div>
        </div>
      </div>
    );
  }

  // Error state
  if (isError || !paymentLinkId) {
    const is404 =
      error && (error as { response?: { status?: number } }).response?.status === 404;

    return (
      <div
        className="flex min-h-screen items-center justify-center"
        style={{ backgroundColor: secondaryColor }}
      >
        <div className="mx-4 max-w-md rounded-xl bg-white p-8 text-center shadow-2xl">
          <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-red-100">
            <svg
              className="h-8 w-8 text-red-600"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </div>
          <h1 className="text-xl font-semibold text-gray-900">
            {t("gymkeePay.checkout.productNotFound")}
          </h1>
          <p className="mt-2 text-gray-600">{getErrorMessage()}</p>
          {!is404 && paymentLinkId && (
            <button
              onClick={() => refetch()}
              className="mt-4 inline-flex items-center gap-2 rounded-lg bg-gray-900 px-5 py-2.5 text-sm font-medium text-white transition-colors hover:bg-gray-800"
            >
              <svg
                className="h-4 w-4"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                />
              </svg>
              {t("gymkeePay.invoice.retry")}
            </button>
          )}
        </div>
      </div>
    );
  }

  // Stripe-init failure — explicit recoverable state. Shown when
  // `loadStripe` returned null (ad-blocker / privacy extension / CSP
  // / network) or when it threw. Without this branch the page would
  // fall through to the `return null` fallback below and the user
  // would see a blank screen with no idea what to do.
  if (stripeInitFailed && !isStripeLoading) {
    return (
      <div
        className="flex min-h-screen items-center justify-center"
        style={{ backgroundColor: secondaryColor }}
      >
        <div className="mx-4 max-w-md rounded-xl bg-white p-8 text-center shadow-2xl">
          <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-amber-100">
            <svg
              className="h-8 w-8 text-amber-600"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
              />
            </svg>
          </div>
          <h1 className="text-xl font-semibold text-gray-900">
            {t("gymkeePay.checkout.stripeUnavailable.title", {
              defaultValue: "We couldn't load the payment form",
            })}
          </h1>
          <p className="mt-2 text-gray-600">
            {t("gymkeePay.checkout.stripeUnavailable.message", {
              defaultValue:
                "An ad-blocker, privacy extension, or network issue is preventing the secure payment form from loading. Please disable any blocker on this page or try a different browser, then reload.",
            })}
          </p>
          <button
            onClick={() => window.location.reload()}
            className="mt-4 inline-flex items-center gap-2 rounded-lg bg-gray-900 px-5 py-2.5 text-sm font-medium text-white transition-colors hover:bg-gray-800"
          >
            <svg
              className="h-4 w-4"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
              />
            </svg>
            {t("gymkeePay.invoice.retry", { defaultValue: "Reload" })}
          </button>
        </div>
      </div>
    );
  }

  // Success state
  if (paymentSuccess && checkoutData) {
    return (
      <CheckoutSuccess
        checkoutData={checkoutData}
        paidAmount={discountedAmount ?? undefined}
        selectedCurrency={selectedCurrency || defaultCurrency}
      />
    );
  }

  // Ready state
  if (checkoutData && stripeInstance) {
    const mainProduct = checkoutData.products[0];
    const mainPrice = getProductPrice(mainProduct);
    const totalAmount = checkoutData.products.reduce(
      (sum, p) => sum + getProductPrice(p).unitAmount,
      0,
    );
    const currency = mainPrice.currency;
    const brandColor = checkoutData.business.brandColor || "#eb5e27";
    const hasMultipleCurrencies = availableCurrencies.length > 1;

    return (
      <div className="min-h-screen" style={{ backgroundColor: secondaryColor }}>
        {checkoutData.business.icon && (
          <link rel="icon" href={checkoutData.business.icon} />
        )}
        <div className="absolute left-0 right-0 top-0">
          <Header
            icon={checkoutData.business.icon}
            businessName={checkoutData.business.name}
            textColor={textColor}
          />
        </div>

        <div className="flex min-h-screen flex-col items-center justify-center px-4 py-20">
          {/* Product Summary Card */}
          <div className="mb-4 w-full max-w-md overflow-hidden rounded-xl bg-white shadow-2xl">
            {/* Cover Image */}
            {mainProduct.branding?.coverImageUrl && (
              <div className="relative h-40 w-full">
                <img
                  src={mainProduct.branding.coverImageUrl}
                  alt={mainProduct.name}
                  className="h-full w-full object-cover"
                />
                {mainProduct.branding?.promotionalBadge && (
                  <span
                    className="absolute left-4 top-4 rounded-full bg-white/90 px-2.5 py-1 text-xs font-medium shadow-sm"
                    style={{ color: brandColor }}
                  >
                    {mainProduct.branding.promotionalBadge}
                  </span>
                )}
              </div>
            )}

            {/* Product info */}
            <div className="p-6">
              <div className="mb-4">
                {!mainProduct.branding?.coverImageUrl &&
                  mainProduct.branding?.promotionalBadge && (
                    <span className="mb-2 inline-block rounded-full bg-orange-100 px-2.5 py-1 text-xs font-medium text-orange-800">
                      {mainProduct.branding.promotionalBadge}
                    </span>
                  )}
                {mainProduct.avatar && mainProduct.avatar.type === "emoji" && (
                  <span className="mb-2 block text-3xl">
                    {mainProduct.avatar.value}
                  </span>
                )}
                <h1 className="text-xl font-semibold text-gray-900">
                  {mainProduct.name}
                </h1>
                {mainProduct.description && (
                  <p className="mt-1 text-sm text-gray-600">
                    {mainProduct.description}
                  </p>
                )}
              </div>

              {/* Price */}
              <div className="flex items-baseline gap-1">
                <p className="text-3xl font-bold text-gray-900">
                  {formatCurrency(discountedAmount ?? totalAmount, currency)}
                </p>
                {discountedAmount !== null &&
                  discountedAmount < totalAmount && (
                    <span className="ml-2 text-lg text-gray-400 line-through">
                      {formatCurrency(totalAmount, currency)}
                    </span>
                  )}
                {checkoutData.mode === "subscription" &&
                  mainPrice.recurring && (
                    <span className="text-sm text-gray-500">
                      /{" "}
                      {mainPrice.recurring.intervalCount > 1
                        ? `${mainPrice.recurring.intervalCount} `
                        : ""}
                      {t(
                        `gymkeePay.frequency.${mainPrice.recurring.interval === "month" ? "monthly" : mainPrice.recurring.interval === "year" ? "yearly" : mainPrice.recurring.interval === "week" ? "weekly" : "daily"}`,
                      ).toLowerCase()}
                    </span>
                  )}
                {hasMultipleCurrencies && (
                  <div className="ml-auto">
                    <CurrencyPicker
                      currencies={availableCurrencies}
                      selectedCurrency={selectedCurrency || defaultCurrency}
                      onCurrencyChange={handleCurrencyChange}
                    />
                  </div>
                )}
              </div>
              {/* Savings message */}
              {discountedAmount !== null && discountedAmount < totalAmount && (
                <p className="mt-1 text-sm font-medium text-green-600">
                  {t("gymkeePay.checkout.youAreSaving", {
                    amount: formatCurrency(
                      totalAmount - discountedAmount,
                      currency,
                    ),
                    percent: Math.round(
                      ((totalAmount - discountedAmount) / totalAmount) * 100,
                    ),
                  })}
                </p>
              )}

              {/* Highlights */}
              {mainProduct.highlights &&
                (mainProduct.highlights.included.length > 0 ||
                  mainProduct.highlights.notIncluded.length > 0) && (
                  <div className="mt-4 space-y-2 border-t border-gray-100 pt-4">
                    {mainProduct.highlights.included.map((item, index) => (
                      <div
                        key={`included-${index}`}
                        className="flex items-center gap-2 text-sm"
                      >
                        <svg
                          className="h-4 w-4 shrink-0 text-green-500"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M5 13l4 4L19 7"
                          />
                        </svg>
                        <span className="text-gray-700">{item.text}</span>
                      </div>
                    ))}
                    {mainProduct.highlights.notIncluded.map((item, index) => (
                      <div
                        key={`not-included-${index}`}
                        className="flex items-center gap-2 text-sm"
                      >
                        <svg
                          className="h-4 w-4 shrink-0 text-gray-400"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M6 18L18 6M6 6l12 12"
                          />
                        </svg>
                        <span className="text-gray-500">{item.text}</span>
                      </div>
                    ))}
                  </div>
                )}

              {/* Multiple products */}
              {checkoutData.products.length > 1 && (
                <div className="mt-4 space-y-2 border-t border-gray-100 pt-4 text-sm">
                  {checkoutData.products.map((product) => {
                    const productPrice = getProductPrice(product);
                    return (
                      <div key={product.id} className="flex justify-between">
                        <span className="text-gray-600">{product.name}</span>
                        <span className="font-medium text-gray-900">
                          {formatCurrency(
                            productPrice.unitAmount,
                            productPrice.currency,
                          )}
                        </span>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>

          {/* Subscription Notice */}
          {checkoutData.mode === "subscription" && (
            <div className="mb-4 w-full max-w-md rounded-xl border border-blue-200 bg-blue-50 p-4 shadow-sm">
              <div className="flex items-start gap-3">
                <svg
                  className="mt-0.5 h-5 w-5 shrink-0 text-blue-600"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                  />
                </svg>
                <div className="text-sm text-blue-700">
                  <p>
                    {t("gymkeePay.checkout.subscriptionNotice", {
                      defaultValue:
                        "This is a recurring subscription. You will be charged automatically each billing period.",
                    })}
                  </p>
                  {(mainPrice.commitment ?? mainProduct.commitment) && (
                    <p className="mt-2 font-medium">
                      {(() => {
                        const commitment = mainPrice.commitment ?? mainProduct.commitment!;
                        return t("gymkeePay.checkout.commitmentNotice", {
                          count: commitment.intervalCount,
                          interval: t(
                            `gymkeePay.commitment.${commitment.interval}`,
                            {
                              count: commitment.intervalCount,
                              defaultValue: commitment.interval,
                            },
                          ),
                          defaultValue: `Minimum commitment: ${commitment.intervalCount} ${commitment.interval}(s)`,
                        });
                      })()}
                    </p>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Payment Form Card */}
          <div className="w-full max-w-md rounded-xl bg-white p-6 shadow-2xl">
            <CheckoutPaymentForm
              key={selectedCurrency || defaultCurrency}
              checkoutData={checkoutData}
              stripeInstance={stripeInstance}
              onSuccess={handlePaymentSuccess}
              onAmountChange={handleAmountChange}
              selectedCurrency={selectedCurrency || defaultCurrency}
              initialFormData={formDataRef.current}
              onFormDataChange={handleFormDataChange}
            />
          </div>

          {/* Footer */}
          <div className="mt-6 text-sm" style={{ color: textMutedColor }}>
            <span>
              {t("gymkeePay.footer.poweredBy")}{" "}
              <span className="font-semibold" style={{ color: textColor }}>
                Gymkee
              </span>
              , {t("gymkeePay.footer.securedBy")}{" "}
              <span className="font-semibold" style={{ color: textColor }}>
                Stripe
              </span>
            </span>
          </div>
        </div>
      </div>
    );
  }

  return null;
}

export default CheckoutPayment;
